// src/composables/useMFA.js
// MFA service for TOTP, Email, and Passkey authentication

import { ref } from 'vue';
import { useAuthStore } from '@/store/AuthStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export function useMFA() {
  const authStore = useAuthStore();
  const isLoading = ref(false);
  const error = ref(null);

  // Helper for authenticated requests
  const authFetch = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (authStore.user_token) {
      headers.Authorization = `Bearer ${authStore.user_token}`;
    }

    // Ensure trailing slash to prevent 301 redirects
    let normalizedEndpoint = endpoint;
    if (!normalizedEndpoint.includes('?') && !normalizedEndpoint.endsWith('/')) {
      normalizedEndpoint += '/';
    }

    const response = await fetch(`${API_URL}${normalizedEndpoint}`, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed');
    }

    // Handle wrapped API response format { success, data, error, meta }
    if (data.success !== undefined && data.data !== undefined) {
      return data.data;
    }

    return data;
  };

  // Get current 2FA status
  const get2FAStatus = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/2fa/status', { method: 'GET' });
      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // --- TOTP Methods ---

  // Start TOTP setup - returns secret and QR code
  const setupTOTP = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/2fa/totp/setup', { method: 'POST' });
      return response; // { secret, qr_code, provisioning_uri }
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Confirm TOTP setup with verification code
  const confirmTOTP = async (code) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/2fa/totp/confirm', {
        method: 'POST',
        body: { code },
      });
      return response; // { backup_codes }
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Disable TOTP
  const disableTOTP = async (code) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/2fa/totp', {
        method: 'DELETE',
        body: { code },
      });
      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // --- Login with 2FA ---

  // Verify 2FA code during login
  const verify2FA = async (challengeToken, code, method = 'totp') => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`${API_URL}/v1/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          challenge_token: challengeToken,
          code,
          method,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Verification failed');
      }

      return data; // { token, refresh_token }
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Request email 2FA code
  const requestEmailCode = async (challengeToken) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`${API_URL}/v1/auth/2fa/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_token: challengeToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send email code');
      }

      return data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // --- Passkey Methods ---

  // Get passkey login options
  const getPasskeyLoginOptions = async (email = null) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`${API_URL}/v1/auth/passkey/login/options/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to get passkey options');
      }

      // Returns { options, challengeToken }
      return data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Verify passkey login
  const verifyPasskeyLogin = async (credential, challengeToken) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await fetch(`${API_URL}/v1/auth/passkey/login/verify/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential,
          challengeToken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Passkey verification failed');
      }

      return data; // { token, refresh_token }
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Get passkey registration options
  const getPasskeyRegisterOptions = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/passkey/register/options/', { method: 'POST' });
      // Returns { options, challengeToken }
      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Complete passkey registration
  const registerPasskey = async (credential, challengeToken, name = 'My Passkey') => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/passkey/register/verify/', {
        method: 'POST',
        body: { credential, challengeToken, name },
      });
      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // List registered passkeys
  const listPasskeys = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch('/v1/auth/passkey/list', { method: 'GET' });
      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Delete a passkey
  const deletePasskey = async (passkeyId) => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authFetch(`/v1/auth/passkey/${passkeyId}`, { method: 'DELETE' });
      return response;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    // Status
    get2FAStatus,
    // TOTP
    setupTOTP,
    confirmTOTP,
    disableTOTP,
    // Login verification
    verify2FA,
    requestEmailCode,
    // Passkey
    getPasskeyLoginOptions,
    verifyPasskeyLogin,
    getPasskeyRegisterOptions,
    registerPasskey,
    listPasskeys,
    deletePasskey,
  };
}
